import { supabase } from './supabase';
import { hashAccessKey } from './accessKey';
import { formatExamIds, parseExamIds, LEGACY_DEFAULT_EXAM_IDS } from '../data/examCatalog';

export interface UserAccessRow {
  id: number;
  user_id: string;
  created_at?: string;
  /** Stored value of user_access.access_key: the salted hash (salt:hash), never the key the user types. */
  access_key: string;
  /** The readable key, so the admin can look it up later; null for users created before this existed. */
  access_key_plain: string | null;
  /** Exams explicitly assigned; null for accounts created before exam assignment existed. */
  exam_ids: number[] | null;
  /** What the learner actually gets on the dashboard. */
  effective_exam_ids: number[];
  /** How many sets the learner may open (lowest set numbers first); null = all sets. */
  no_of_sets: number | null;
}

export const MISSING_COLUMN_HELP =
  "A column is missing in the user_access table (exam_ids, access_key_plain and/or no_of_sets). Run supabase/user_access_exams.sql and supabase/user_access_no_of_sets.sql in the Supabase SQL editor, then try again.";

const missingColumn = (err: { message?: string; code?: string } | null, column: string): boolean =>
  !!err && (err.message || '').includes(column) && (err.code === '42703' || err.code === 'PGRST204' || /column|schema cache/i.test(err.message || ''));

const toRow = (r: Record<string, unknown>): UserAccessRow => {
  const ids = parseExamIds(r.exam_ids);
  return {
    id: r.id as number,
    user_id: r.user_id as string,
    created_at: r.created_at as string | undefined,
    access_key: (r.access_key as string) || '',
    access_key_plain: (r.access_key_plain as string) || null,
    exam_ids: ids,
    effective_exam_ids: ids ?? LEGACY_DEFAULT_EXAM_IDS,
    no_of_sets: typeof r.no_of_sets === 'number' && r.no_of_sets > 0 ? r.no_of_sets : null,
  };
};

export async function fetchUserAccessList(): Promise<{ success: boolean; users?: UserAccessRow[]; examColumnMissing?: boolean; plainColumnMissing?: boolean; setsColumnMissing?: boolean; error?: string }> {
  // Newer columns may not exist yet: drop whichever one the database reports as missing and retry.
  const columns = ['id', 'user_id', 'access_key', 'access_key_plain', 'exam_ids', 'no_of_sets', 'created_at'];
  let examColumnMissing = false;
  let plainColumnMissing = false;
  let setsColumnMissing = false;
  for (let attempt = 0; attempt < 4; attempt++) {
    const res = await supabase.from('user_access').select(columns.join(', ')).order('id', { ascending: false });
    if (!res.error) {
      return { success: true, users: (res.data || []).map(r => toRow(r as unknown as Record<string, unknown>)), examColumnMissing, plainColumnMissing, setsColumnMissing };
    }
    if (missingColumn(res.error, 'exam_ids') && columns.includes('exam_ids')) {
      columns.splice(columns.indexOf('exam_ids'), 1);
      examColumnMissing = true;
    } else if (missingColumn(res.error, 'access_key_plain') && columns.includes('access_key_plain')) {
      columns.splice(columns.indexOf('access_key_plain'), 1);
      plainColumnMissing = true;
    } else if (missingColumn(res.error, 'no_of_sets') && columns.includes('no_of_sets')) {
      columns.splice(columns.indexOf('no_of_sets'), 1);
      setsColumnMissing = true;
    } else {
      return { success: false, error: res.error.message };
    }
  }
  return { success: false, error: 'Could not load users.' };
}

const dbError = (error: { message?: string; code?: string }): string =>
  missingColumn(error, 'exam_ids') || missingColumn(error, 'access_key_plain') || missingColumn(error, 'no_of_sets') ? MISSING_COLUMN_HELP : error.message || 'Database error';

export async function createUserAccess(input: { userId: string; examIds: number[]; accessKey: string; accessKeyPlain: string; noOfSets?: number | null }): Promise<{ success: boolean; error?: string }> {
  const userId = input.userId.trim();
  const existing = await supabase.from('user_access').select('id').eq('user_id', userId);
  if (existing.error) return { success: false, error: existing.error.message };
  if (existing.data && existing.data.length > 0) return { success: false, error: `The user ID "${userId}" already exists.` };

  const access_key = await hashAccessKey(input.accessKey);
  const { error } = await supabase.from('user_access').insert({ user_id: userId, access_key, access_key_plain: input.accessKeyPlain, exam_ids: formatExamIds(input.examIds), ...(input.noOfSets ? { no_of_sets: input.noOfSets } : {}) });
  if (error) return { success: false, error: dbError(error) };
  return { success: true };
}

export async function updateUserExams(id: number, examIds: number[]): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.from('user_access').update({ exam_ids: formatExamIds(examIds) }).eq('id', id).select('id');
  if (error) return { success: false, error: dbError(error) };
  if (!data || data.length === 0) return { success: false, error: 'Nothing was updated (the user was not found, or updates are not allowed by the database policy).' };
  return { success: true };
}

export async function updateUserSetLimit(id: number, noOfSets: number | null): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.from('user_access').update({ no_of_sets: noOfSets && noOfSets > 0 ? noOfSets : null }).eq('id', id).select('id');
  if (error) return { success: false, error: dbError(error) };
  if (!data || data.length === 0) return { success: false, error: 'Nothing was updated (the user was not found, or updates are not allowed by the database policy).' };
  return { success: true };
}

/** Issues a new key: replaces the login hash and the readable copy together. */
export async function resetUserKey(id: number, accessKey: string): Promise<{ success: boolean; error?: string }> {
  const access_key = await hashAccessKey(accessKey);
  const { data, error } = await supabase.from('user_access').update({ access_key, access_key_plain: accessKey }).eq('id', id).select('id');
  if (error) return { success: false, error: dbError(error) };
  if (!data || data.length === 0) return { success: false, error: 'Nothing was updated (the user was not found, or updates are not allowed by the database policy).' };
  return { success: true };
}

/** Saves the readable copy of a key the admin already knows (changes nothing about the login). */
export async function saveKnownKey(id: number, accessKey: string): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.from('user_access').update({ access_key_plain: accessKey }).eq('id', id).select('id');
  if (error) return { success: false, error: dbError(error) };
  if (!data || data.length === 0) return { success: false, error: 'Nothing was updated (the user was not found, or updates are not allowed by the database policy).' };
  return { success: true };
}

export async function deleteUserAccess(id: number): Promise<{ success: boolean; error?: string }> {
  const { data, error } = await supabase.from('user_access').delete().eq('id', id).select('id');
  if (error) return { success: false, error: error.message };
  if (!data || data.length === 0) return { success: false, error: 'Nothing was deleted (the user was not found, or deletes are not allowed by the database policy).' };
  return { success: true };
}

/** Used by the learner login: the stored hash plus the exams this user may open. */
export async function fetchLoginRecord(userId: string): Promise<{ found: boolean; accessKey?: string; examIds?: number[]; noOfSets?: number | null; error?: string }> {
  let res = await supabase.from('user_access').select('user_id, access_key, exam_ids, no_of_sets').eq('user_id', userId);
  if (res.error && missingColumn(res.error, 'no_of_sets')) {
    res = (await supabase.from('user_access').select('user_id, access_key, exam_ids').eq('user_id', userId)) as typeof res;
  }
  if (res.error && missingColumn(res.error, 'exam_ids')) {
    res = (await supabase.from('user_access').select('user_id, access_key').eq('user_id', userId)) as typeof res;
  }
  if (res.error) return { found: false, error: res.error.message };
  if (!res.data || res.data.length === 0) return { found: false };
  const row = res.data[0] as unknown as { access_key: string; exam_ids?: unknown; no_of_sets?: unknown };
  return { found: true, accessKey: row.access_key, examIds: parseExamIds(row.exam_ids) ?? LEGACY_DEFAULT_EXAM_IDS, noOfSets: typeof row.no_of_sets === 'number' && row.no_of_sets > 0 ? row.no_of_sets : null };
}
